import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

interface ShipmentCounters {
	enTransito: number;
	eta7: number;
	eta2: number;
	confirmacionArribo: number;
}

interface DashboardState {
	pendingInvoices: string | number | null;
	activeQuotes: string | number | null;
	activeShipments: string | number | null;
	shipmentCounters: ShipmentCounters;
	isLoadingCounters: boolean;
	quotesActivityExpanded: {
		quoting: boolean;
		sent: boolean;
	};
	lastUpdated: {
		pendingInvoices?: number;
		activeQuotes?: number;
		activeShipments?: number;
		shipmentCounters?: number;
	};
}

export type QuotesActivityPanel = "quoting" | "sent";

const initialState: DashboardState = {
	pendingInvoices: null,
	activeQuotes: null,
	activeShipments: null,
	shipmentCounters: {
		enTransito: 0,
		eta7: 0,
		eta2: 0,
		confirmacionArribo: 0,
	},
	isLoadingCounters: true,
	quotesActivityExpanded: {
		quoting: false,
		sent: false,
	},
	lastUpdated: {},
};

export const dashboardSlice = createSlice({
	name: "dashboard",
	initialState,
	reducers: {
		setPendingInvoices: (state, action: PayloadAction<string | number>) => {
			state.pendingInvoices = action.payload;
			state.lastUpdated.pendingInvoices = Date.now();
		},
		setActiveQuotes: (state, action: PayloadAction<string | number>) => {
			state.activeQuotes = action.payload;
			state.lastUpdated.activeQuotes = Date.now();
		},
		setActiveShipments: (state, action: PayloadAction<string | number>) => {
			state.activeShipments = action.payload;
			state.lastUpdated.activeShipments = Date.now();
		},
		setShipmentCounters: (state, action: PayloadAction<ShipmentCounters>) => {
			state.shipmentCounters = action.payload;
			state.lastUpdated.shipmentCounters = Date.now();
		},
		setIsLoadingCounters: (state, action: PayloadAction<boolean>) => {
			state.isLoadingCounters = action.payload;
		},
		setQuotesActivityExpanded: (
			state,
			action: PayloadAction<{ panel: QuotesActivityPanel; expanded: boolean }>,
		) => {
			state.quotesActivityExpanded[action.payload.panel] =
				action.payload.expanded;
		},
		toggleQuotesActivityExpanded: (
			state,
			action: PayloadAction<QuotesActivityPanel>,
		) => {
			state.quotesActivityExpanded[action.payload] =
				!state.quotesActivityExpanded[action.payload];
		},
		clearDashboardData: (state) => {
			state.pendingInvoices = null;
			state.activeQuotes = null;
			state.activeShipments = null;
			state.shipmentCounters = {
				enTransito: 0,
				eta7: 0,
				eta2: 0,
				confirmacionArribo: 0,
			};
			state.isLoadingCounters = true;
			state.lastUpdated = {};
		},
	},
});

export const {
	setPendingInvoices,
	setActiveQuotes,
	setActiveShipments,
	setShipmentCounters,
	setIsLoadingCounters,
	setQuotesActivityExpanded,
	toggleQuotesActivityExpanded,
	clearDashboardData,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
